/** Nombre que manda el servidor en Content-Disposition. */
export function filenameFromContentDisposition(header: string | null, fallback: string): string {
  const ensure = (name: string) => {
    const clean = name.replace(/[/\\]/g, "").trim() || "comprobante.pdf";
    return clean.toLowerCase().endsWith(".pdf") ? clean : `${clean}.pdf`;
  };
  if (!header) return ensure(fallback);
  const star = /filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;\s]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return ensure(decodeURIComponent(star[1].replace(/"/g, "").trim()));
    } catch {
      /* el filename plano sigue */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  if (plain?.[1]) return ensure(plain[1].trim());
  return ensure(fallback);
}

export function namedPdfFile(blob: Blob, filename: string): File {
  return new File([blob], filenameFromContentDisposition(null, filename), { type: "application/pdf" });
}

export type PdfViewerEngine = "firefox" | "safari" | "chromium";

/** Firefox deja la dirección temporal. Safari necesita el nombre en la URL. */
export function pdfViewerEngine(ua: string): PdfViewerEngine {
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\/|Vivaldi\/|CriOS\//.test(ua)) {
    return "safari";
  }
  return "chromium";
}

/**
 * Dirección de la pestaña. Firefox devuelve null: sigue con el archivo en memoria.
 * Chromium abre /pdf/. Safari agrega el nombre al final.
 */
export function pdfTabUrl(
  engine: PdfViewerEngine,
  path: string,
  filename: string,
  query?: string,
): string | null {
  if (engine === "firefox") return null;
  const clean = path.startsWith("/") ? path : `/${path}`;
  const base = clean.endsWith("/") ? clean : `${clean}/`;
  const q = query ? (query.startsWith("?") ? query : `?${query}`) : "";
  if (engine === "safari") {
    const name = (filename.split(/[/\\]/).pop() || "comprobante.pdf").trim();
    return `${base}${encodeURIComponent(name)}${q}`;
  }
  return `${base}${q}`;
}

async function grantPdfView(path: string): Promise<boolean> {
  const token = window.localStorage.getItem("authToken");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch("/api/pdf-view/", {
    method: "POST",
    credentials: "same-origin",
    headers,
    body: JSON.stringify({ path }),
  });
  return response.ok;
}

function downloadNamed(url: string, name: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export type PdfTabView = { path: string; query?: string };

/**
 * Firefox abre el archivo en memoria: el visor ya usa su nombre.
 * Chromium y Safari abren la respuesta del servidor. El pase va en un
 * cookie, no en la dirección. Si la pestaña se bloquea, descarga con el nombre.
 */
export async function openPdfTab(
  file: Blob,
  filename?: string,
  view?: PdfTabView,
): Promise<Window | null> {
  const named =
    file instanceof File && file.name
      ? file
      : namedPdfFile(file, filename || "comprobante.pdf");
  const engine = pdfViewerEngine(window.navigator.userAgent);
  const target = view?.path ? pdfTabUrl(engine, view.path, named.name, view.query) : null;
  if (target) {
    const granted = await grantPdfView(view!.path);
    if (granted) {
      const win = window.open(target, "_blank");
      if (win) return win;
    }
  }
  const url = URL.createObjectURL(named);
  const win = window.open(url, "_blank");
  if (!win) {
    downloadNamed(url, named.name);
    window.setTimeout(() => URL.revokeObjectURL(url), 1500);
  }
  return win;
}
